<div
  class="account-settings-embedded pathways-page account-settings-main"
  [class.account-settings-dialog-body]="dialogMode()"
>
  <div class="pathways-page-inner account-settings-inner">
    @if (!dialogMode()) {
      <div class="pathways-page-header account-settings-header">
        <div>
          <h1>Account Settings</h1>
          <p class="pathways-page-intro account-settings-intro">
            Manage your profile preferences for Pathways-AI.
          </p>
        </div>
      </div>
    } @else {
      <div class="account-settings-dialog-heading">
        <h2>{{ dialogHeadingTitle() }}</h2>
        <p class="account-settings-intro">{{ dialogHeadingIntro() }}</p>
      </div>
    }

    @if (showAppearance()) {
      <section class="appearance-panel pathways-settings-card" aria-label="Appearance">
        <div class="appearance-group">
          <p class="form-label">Theme</p>
          <p class="field-hint field-hint-below-legend">
            Choose light, dark, or match your device setting.
          </p>
          <div class="tile-picker" role="radiogroup" aria-label="Theme">
            @for (opt of themeOptions; track opt.id) {
              <button
                type="button"
                class="picker-tile"
                role="radio"
                [class.selected]="draftThemePreference() === opt.id"
                [attr.aria-checked]="draftThemePreference() === opt.id"
                (click)="selectTheme(opt.id)"
              >
                <i [class]="opt.icon" aria-hidden="true"></i>
                <span>{{ opt.label }}</span>
              </button>
            }
          </div>
        </div>

        <div class="appearance-group">
          <p class="form-label">Font size</p>
          <p class="field-hint field-hint-below-legend">
            Adjust base text size across the app.
          </p>
          <div class="tile-picker tile-picker-compact" role="radiogroup" aria-label="Font size">
            @for (opt of fontSizeOptions; track opt.id) {
              <button
                type="button"
                class="picker-tile picker-tile-font"
                role="radio"
                [class.selected]="draftFontSize() === opt.id"
                [attr.aria-checked]="draftFontSize() === opt.id"
                [attr.aria-label]="'Font size ' + opt.label"
                (click)="selectFontSize(opt.id)"
              >
                <span class="font-size-label">{{ opt.label }}</span>
              </button>
            }
          </div>
        </div>
      </section>
    }

    @if (showAccountPrefsForm() && initialLoading()) {
      <div class="account-settings-loading" aria-busy="true" aria-live="polite">
        <ion-spinner name="crescent" aria-hidden="true"></ion-spinner>
        <p>Loading your settings…</p>
      </div>
    } @else if (showAccountPrefsForm()) {
      <form class="account-settings-form pathways-settings-card" (submit)="onFormSubmit($event)">
        @if (saveError(); as err) {
          <p class="field-error-banner" role="alert">{{ err }}</p>
        }

        @if (!dialogMode() || activeTab() === 'profile') {
          @if (identityFieldsDisabled) {
            <p class="identity-sso-hint" role="note">
              Your name and email are managed by your organization sign-in (SSO) and
              cannot be changed here.
            </p>
          }

          <div class="form-row form-row-name">
            <div class="form-field">
              <label class="form-label" for="acct-first-name">First Name</label>
              <input
                id="acct-first-name"
                type="text"
                class="form-input"
                [class.form-input-disabled]="identityFieldsDisabled"
                [class.invalid]="
                  !identityFieldsDisabled && saveError() && !firstName().trim()
                "
                [value]="firstName()"
                [disabled]="identityFieldsDisabled"
                (input)="firstName.set($any($event.target).value); saveError.set(null)"
                autocomplete="given-name"
                [attr.required]="identityFieldsDisabled ? null : ''"
              />
            </div>
            <div class="form-field">
              <label class="form-label" for="acct-last-name">Last Name</label>
              <input
                id="acct-last-name"
                type="text"
                class="form-input"
                [class.form-input-disabled]="identityFieldsDisabled"
                [class.invalid]="
                  !identityFieldsDisabled && saveError() && !lastName().trim()
                "
                [value]="lastName()"
                [disabled]="identityFieldsDisabled"
                (input)="lastName.set($any($event.target).value); saveError.set(null)"
                autocomplete="family-name"
                [attr.required]="identityFieldsDisabled ? null : ''"
              />
            </div>
          </div>

          <div class="form-row form-row-name">
            <div class="form-field">
              <label class="form-label" for="acct-email">Email Address</label>
              <input
                id="acct-email"
                type="email"
                class="form-input form-input-readonly"
                [class.form-input-disabled]="identityFieldsDisabled"
                [value]="emailDisplay()"
                [disabled]="identityFieldsDisabled"
                [readonly]="!identityFieldsDisabled"
                tabindex="-1"
                aria-readonly="true"
              />
            </div>
            <div class="form-field">
              <label class="form-label" for="acct-role">Role</label>
              <input
                id="acct-role"
                type="text"
                class="form-input form-input-readonly form-input-disabled"
                [value]="roleDisplayLabel()"
                disabled
              />
            </div>
          </div>

          @if (workIqEnabled) {
            <section class="connected-accounts" aria-labelledby="acct-connected-heading">
              <h3 id="acct-connected-heading" class="connected-accounts-heading">Connected accounts</h3>
              <p class="field-hint">
                Connect Microsoft to use workIQ features. This does not change your Pathways sign-in.
              </p>
              @if (workIqStatusError(); as workIqErr) {
                <p class="field-error-banner" role="alert">{{ workIqErr }}</p>
              }
              @if (workIq.needsReconnect()) {
                <p class="field-error-banner" role="status">
                  Microsoft authorization expired. Reconnect to continue using workIQ.
                </p>
              }
              @if (workIqStatusLoading()) {
                <p class="field-hint">Loading connection status…</p>
              } @else if (workIq.connectionStatus().connected) {
                <div class="connected-accounts-row">
                  <p class="connected-accounts-status">
                    Connected as
                    <strong>{{ workIq.connectionStatus().entraUpn || 'Microsoft account' }}</strong>
                  </p>
                  <button
                    type="button"
                    class="pathways-btn-secondary"
                    [disabled]="workIq.actionBusy() || workIqProbeBusy()"
                    (click)="onDisconnectMicrosoft()"
                  >
                    Disconnect
                  </button>
                </div>
                <!-- PAT-338 Phase B harness: exercise dual-auth query; not product UI -->
                <div class="connected-accounts-row workiq-probe-row">
                  <button
                    type="button"
                    class="pathways-btn-secondary"
                    data-testid="workiq-verify-query"
                    [disabled]="workIq.actionBusy() || workIqProbeBusy()"
                    (click)="onVerifyWorkIqQuery()"
                  >
                    @if (workIqProbeBusy()) {
                      Verifying…
                    } @else {
                      Verify workIQ query
                    }
                  </button>
                  @if (workIqProbeOk()) {
                    <p class="field-hint" role="status">Last query check succeeded.</p>
                  }
                </div>
              } @else {
                <div class="connected-accounts-row">
                  <p class="connected-accounts-status">Not connected</p>
                  <button
                    type="button"
                    class="pathways-btn-primary"
                    [disabled]="workIqUiBusy()"
                    (click)="onConnectMicrosoft()"
                  >
                    Connect Microsoft account
                  </button>
                </div>
              }

              <!-- PAT-340 — first product action: search + summary -->
              <div class="workiq-action-panel" data-testid="workiq-action-panel">
                <h4 class="workiq-action-heading" id="workiq-action-heading">workIQ actions</h4>
                <p class="field-hint">
                  Search Microsoft 365 work data or request a suggested summary. Results are AI-suggested —
                  review before relying on them.
                </p>
                <label class="form-label" for="workiq-action-query">Topic or question</label>
                <textarea
                  id="workiq-action-query"
                  class="form-input workiq-action-query"
                  rows="3"
                  maxlength="4000"
                  [value]="workIqActionQuery()"
                  [disabled]="workIqUiBusy()"
                  (input)="onWorkIqQueryInput($event)"
                  placeholder="e.g. Q1 project emails or weekly Pathways status"
                ></textarea>
                <div class="workiq-action-buttons">
                  <button
                    type="button"
                    class="pathways-btn-primary"
                    data-testid="workiq-search-btn"
                    [disabled]="workIqUiBusy()"
                    (click)="onRunWorkIqAction('search')"
                  >
                    @if (workIqActionBusy() && workIqLastAction() === 'search') {
                      Searching…
                    } @else {
                      Search
                    }
                  </button>
                  <button
                    type="button"
                    class="pathways-btn-secondary"
                    data-testid="workiq-summary-btn"
                    [disabled]="workIqUiBusy()"
                    (click)="onRunWorkIqAction('summary')"
                  >
                    @if (workIqActionBusy() && workIqLastAction() === 'summary') {
                      Summarizing…
                    } @else {
                      Summary
                    }
                  </button>
                </div>
                @if (workIqActionError(); as actionErr) {
                  <p class="field-error-banner" role="alert" data-testid="workiq-action-error">
                    {{ actionErr }}
                  </p>
                }
                @if (workIqActionResult(); as actionResult) {
                  <div
                    class="workiq-action-result"
                    role="region"
                    aria-labelledby="workiq-result-heading"
                    data-testid="workiq-action-result"
                  >
                    <h5 id="workiq-result-heading" class="workiq-result-heading">
                      Suggested {{ workIqLastAction() === 'summary' ? 'summary' : 'search result' }}
                    </h5>
                    <p class="workiq-result-disclosure">
                      AI-suggested output from workIQ — not confirmed. Review before use.
                    </p>
                    <pre class="workiq-result-body">{{ actionResult }}</pre>
                  </div>
                }
              </div>
            </section>
          }
        }

        @if (!dialogMode() || activeTab() === 'preferences') {
          <div class="form-row form-row-full">
            <div class="form-field">
              <label class="form-label" for="acct-default-model">Default Model</label>
              @if (modelsLoading()) {
                <p class="field-hint">Loading models…</p>
              } @else if (modelLoadError()) {
                <p class="field-error-banner" role="alert">{{ modelLoadError() }}</p>
                <button type="button" class="pathways-btn-secondary retry-models-btn" (click)="retryModels()">
                  Retry
                </button>
              } @else {
                <select
                  id="acct-default-model"
                  class="form-select"
                  (change)="defaultModelKey.set($any($event.target).value)"
                >
                  @for (m of modelRows(); track m.id) {
                    <option [value]="m.id" [selected]="m.id === defaultModelKey()">
                      {{ m.label }}
                    </option>
                  }
                </select>
              }
            </div>
          </div>

          <fieldset class="form-row form-row-full file-types-fieldset">
            <legend class="form-label">File Upload Types</legend>
            <p class="field-hint field-hint-below-legend">
              Managed by your organization administrator in the Admin console.
              The file picker only shows the enabled types.
            </p>
            <ul class="checkbox-grid" aria-label="File Upload Types">
              @for (opt of fileUploadOptions; track opt.id) {
                <li class="checkbox-cell">
                  <label class="checkbox-row">
                    <input
                      type="checkbox"
                      [checked]="isFileTypeChecked(opt.id)"
                      disabled
                    />
                    <span>{{ opt.label }}</span>
                  </label>
                </li>
              }
            </ul>
          </fieldset>
        }

        @if (!dialogMode()) {
          <div class="form-actions">
            <button type="submit" class="pathways-btn-primary save-settings-btn">Save Changes</button>
          </div>
        }
      </form>
    }
  </div>
</div>
